import FnlSummariesTokenV4 from "~/schemas/dbt/FnlSummariesTokenV4";

// // // //

export interface Token extends FnlSummariesTokenV4 {
    issuance_type: "NATIVE" | "BRIDGE" | null;
    protocol: {
        id: number;
        slug: string;
        name: string;
        color_hex: string | null;
        icon_url: string | null;
        website: string;
    };
    base_protocol: {
        id: number;
        slug: string;
        name: string;
        color_hex: string | null;
        icon_url: string | null;
        website: string;
    } | null;
    platform: {
        id: number;
        slug: string;
        name: string;
        color_hex: string | null;
        icon_url: string | null;
        website: string;
        tokenization_type: string | null;
        issuance_type?: "NATIVE" | "BRIDGE" | null;
    } | null;
    network: {
        id: number;
        slug: string;
        name: string;
        icon_url: string | null;
        color_hex: string | null;
    };
    parent_network_id: number | null;
    parent_network: {
        id: number;
        slug: string;
        name: string;
        icon_url: string | null;
        color_hex: string | null;
    } | null;
    asset_manager_id: number | null;
    asset_manager: {
        id: number;
        slug: string;
        name: string;
        icon_url: string | null;
        color_hex: string | null;
    } | null;
    asset: {
        id: number;
        slug: string;
        name: string;
        ticker: null | string;
        icon_url: null | string;
        color_hex: null | string;
        governing_body: null | string;
        regulatory_framework: null | string;
        // NOTE - the asset metadata below is not yet returned by /v4/tokens,
        // so it stays optional until the API exposes it on the token's asset
        pegged_asset?: {
            id: number | null;
            name: string | null;
            slug: string | null;
            ticker: string | null;
            icon_url: string | null;
            color_hex: string | null;
            type: string | null;
            description: string | null;
        } | null;
        jurisdiction_country?: {
            id: number | null;
            name: string | null;
            slug: string | null;
            color_hex: string | null;
            icon_url: string | null;
        } | null;
        real_estate_property?: {
            country_id: number | null;
            country: {
                name: string | null;
                slug: string | null;
                icon_url: string | null;
                color_hex: string | null;
            };
        } | null;
    };
    asset_class: null | {
        id: number;
        slug: string;
        name: string;
        icon_url: null | string;
        color_hex: null | string;
    };
    issuer: {
        id: number;
        slug: string;
        name: string;
        icon_url: null | string;
        color_hex: null | string;
        legal_name: null | string;
        cik: null | string;
        lei: null | string;
        legal_structure_type: null | string;
        legal_structure_country: null | string;
        legal_structure_state: null | string;
        legal_structure_country_id: null | number;
        legal_structure_country_icon_url: null | string;
    };
    standards: string[] | null;
}

// // // //

export enum TokenColumns {
    search = "search",
    id = "id",
    name = "name",
    address = "address",
    standard = "standard",
    asset_id = "asset_id",
    asset_ticker = "asset_ticker",
    asset_name = "asset_name",
    network_id = "network_id",
    network_slug = "network_slug",
    protocol_id = "protocol_id",
    protocol_slug = "protocol_slug",
    base_protocol_id = "base_protocol_id",
    base_protocol_slug = "base_protocol_slug",
    tokenization_type_id = "tokenization_type_id",
    asset_class_id = "asset_class_id",
    asset_class_slug = "asset_class_slug",
    asset_regulatory_framework = "asset_regulatory_framework",
    is_investable = "is_investable",
    manager_id = "manager_id",
    manager_slug = "manager_slug",
    issuer_id = "issuer_id",
    issuer_legal_structure_country_name = "issuer_legal_structure_country_name",
    // Measure Columns
    adjusted_holding_addresses_count = "adjusted_holding_addresses_count",
    annually_active_addresses_count = "annually_active_addresses_count",
    available_capacity_dollar = "available_capacity_dollar",
    available_capacity_token = "available_capacity_token",
    bridged_token_market_cap_dollar = "bridged_token_market_cap_dollar",
    bridged_token_supply_token = "bridged_token_supply_token",
    bridged_token_value_dollar = "bridged_token_value_dollar",
    cash_drag_percent = "cash_drag_percent",
    circulating_asset_value_dollar = "circulating_asset_value_dollar",
    circulating_market_value_dollar = "circulating_market_value_dollar",
    circulating_supply_token = "circulating_supply_token",
    cumulative_interest_earned_dollar = "cumulative_interest_earned_dollar",
    cumulative_interest_earned_token = "cumulative_interest_earned_token",
    cumulative_issued_amount_dollar = "cumulative_issued_amount_dollar",
    cumulative_issued_amount_token = "cumulative_issued_amount_token",
    cumulative_repaid_principal_dollar = "cumulative_repaid_principal_dollar",
    cumulative_repaid_principal_token = "cumulative_repaid_principal_token",
    daily_active_addresses_count = "daily_active_addresses_count",
    daily_burns_dollar = "daily_burns_dollar",
    daily_burns_token = "daily_burns_token",
    daily_mints_dollar = "daily_mints_dollar",
    daily_mints_token = "daily_mints_token",
    daily_transfer_volume_dollar = "daily_transfer_volume_dollar",
    daily_transfer_volume_token = "daily_transfer_volume_token",
    defaulted_loans_amount_dollar = "defaulted_loans_amount_dollar",
    defaulted_loans_amount_percent = "defaulted_loans_amount_percent",
    drawndown_assets_dollar = "drawndown_assets_dollar",
    drawndown_assets_token = "drawndown_assets_token",
    funded_assets_dollar = "funded_assets_dollar",
    funded_assets_token = "funded_assets_token",
    holding_addresses_count = "holding_addresses_count",
    idle_capital_dollar = "idle_capital_dollar",
    idle_capital_token = "idle_capital_token",
    interest_paid_dollar = "interest_paid_dollar",
    interest_paid_token = "interest_paid_token",
    market_value_dollar = "market_value_dollar",
    maximum_capacity_dollar = "maximum_capacity_dollar",
    monthly_active_addresses_count = "monthly_active_addresses_count",
    monthly_transfer_count = "monthly_transfer_count",
    monthly_transfer_volume = "monthly_transfer_volume",
    net_asset_value_dollar = "net_asset_value_dollar",
    net_asset_value_token = "net_asset_value_token",
    number_of_active_loans = "number_of_active_loans",
    number_of_daily_transactions_count = "number_of_daily_transactions_count",
    number_of_defaulted_loans = "number_of_defaulted_loans",
    number_of_loans_funded = "number_of_loans_funded",
    outstanding_capital_dollar = "outstanding_capital_dollar",
    outstanding_principal_dollar = "outstanding_principal_dollar",
    outstanding_principal_token = "outstanding_principal_token",
    price_dollar = "price_dollar",
    principal_paid_dollar = "principal_paid_dollar",
    principal_paid_token = "principal_paid_token",
    quarterly_active_addresses_count = "quarterly_active_addresses_count",
    recovered_assets_dollar = "recovered_assets_dollar",
    recovered_assets_token = "recovered_assets_token",
    total_asset_value_dollar = "total_asset_value_dollar",
    total_asset_value_token = "total_asset_value_token",
    total_supply_token = "total_supply_token",
    trailing_30_day_active_addresses_count = "trailing_30_day_active_addresses_count",
    trailing_30_day_transfer_count = "trailing_30_day_transfer_count",
    trailing_30_day_transfer_volume = "trailing_30_day_transfer_volume",
    trailing_365_day_active_addresses_count = "trailing_365_day_active_addresses_count",
    trailing_7_day_active_addresses_count = "trailing_7_day_active_addresses_count",
    trailing_7_day_transfer_count = "trailing_7_day_transfer_count",
    trailing_7_day_transfer_volume = "trailing_7_day_transfer_volume",
    trailing_90_day_active_addresses_count = "trailing_90_day_active_addresses_count",
    weekly_active_addresses_count = "weekly_active_addresses_count",
    weekly_transfer_count = "weekly_transfer_count",
    weekly_transfer_volume = "weekly_transfer_volume",
    weighted_average_maturity = "weighted_average_maturity",
    written_off_principal_dollar = "written_off_principal_dollar",
    written_off_principal_token = "written_off_principal_token",
    yield_to_maturity = "yield_to_maturity",
}
